import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Upload, X } from 'lucide-react';
import { exerciseAPI } from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import { ColorPalette } from './ColorPalette';
import { FONT_SIZE_OPTIONS, resolveFontSize } from '../config/fontSizes';

export const ExerciseEditor = ({ exercise, topicId, onClose, onSave }) => {
  const { t } = useLanguage();
  const [exerciseType, setExerciseType] = useState('flashcard');
  
  // Flashcard state
  const [pairs, setPairs] = useState([{ 
    german: '', 
    russian: '', 
    germanTextColor: '#7c3aed', 
    germanFontSize: 'text-2xl',
    russianTextColor: '#000000',
    russianFontSize: 'text-2xl',
    germanDescription: '',
    germanDescriptionColor: '#000000',
    germanDescriptionFontSize: 'text-base',
    russianDescription: '',
    russianDescriptionColor: '#000000',
    russianDescriptionFontSize: 'text-base'
  }]);
  const [currentPairIndex, setCurrentPairIndex] = useState(0);
  const [activeField, setActiveField] = useState('german'); // 'german' or 'russian'
  
  // ImageChoice state
  const [imageChoiceContent, setImageChoiceContent] = useState({
    question: '',
    questionImage: null,
    textColor: '#7c3aed',
    fontSize: 'text-2xl',
    options: [
      { text: '', image: null, description: '' },
      { text: '', image: null, description: '' },
    ],
    correctAnswerIndex: 0,
  });
  
  // MultipleChoice state
  const [multipleChoiceContent, setMultipleChoiceContent] = useState({
    question: '', // Немецкое слово
    options: [
      { text: '' }, // Варианты переводов
      { text: '' },
      { text: '' },
      { text: '' }
    ],
    correctAnswerIndex: 0
  });


  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (exercise) {
      console.log('Loading exercise for editing:', exercise);
      setExerciseType(exercise.type || 'flashcard');
      
      if (exercise.type === 'flashcard') {
        // Pairs могут быть в exercise.content.pairs или напрямую в exercise.pairs
        const content = exercise.content || {};
        const pairsData = content.pairs || exercise.pairs || [];
        
        console.log('Flashcard pairs data:', pairsData);
        
        setPairs(pairsData.length > 0 
          ? pairsData.map(p => ({ 
              german: p.german, 
              russian: p.english,
              germanTextColor: p.germanTextColor || p.textColor || '#7c3aed',
              germanFontSize: p.germanFontSize || p.fontSize || 'text-2xl',
              russianTextColor: p.russianTextColor || '#000000',
              russianFontSize: p.russianFontSize || 'text-2xl',
              germanDescription: p.germanDescription || '',
              germanDescriptionColor: p.germanDescriptionColor || '#000000',
              germanDescriptionFontSize: p.germanDescriptionFontSize || 'text-base',
              russianDescription: p.russianDescription || '',
              russianDescriptionColor: p.russianDescriptionColor || '#000000',
              russianDescriptionFontSize: p.russianDescriptionFontSize || 'text-base'
            }))
          : [{ german: '', russian: '', germanTextColor: '#7c3aed', germanFontSize: 'text-2xl', russianTextColor: '#000000', russianFontSize: 'text-2xl', germanDescription: '', germanDescriptionColor: '#000000', germanDescriptionFontSize: 'text-base', russianDescription: '', russianDescriptionColor: '#000000', russianDescriptionFontSize: 'text-base' }]
        );
      } else if (exercise.type === 'imageChoice') {
        // Для imageChoice данные могут быть в exercise.content или напрямую в exercise
        const content = exercise.content || {};
        const optionsData = content.options || exercise.options || [];
        
        console.log('ImageChoice options data:', optionsData);
        console.log('ImageChoice question:', content.question || exercise.question);
        
        setImageChoiceContent({
          question: content.question || exercise.question || '',
          questionImage: content.questionImage || exercise.questionImage || null,
          textColor: content.textColor || exercise.textColor || '#7c3aed',
          fontSize: content.fontSize || exercise.fontSize || 'text-2xl',
          options: optionsData.length > 0 
            ? optionsData.map(o => ({
                text: o.text || '',
                image: o.image || null,
                description: o.description || ''
              }))
            : [
                { text: '', image: null, description: '' },
                { text: '', image: null, description: '' },
              ],
          correctAnswerIndex: content.correctAnswerIndex !== undefined ? content.correctAnswerIndex : (exercise.correctAnswerIndex || 0),
        });
      } else if (exercise.type === 'multipleChoice') {
        const content = exercise.content || {};
        const optionsData = content.options || exercise.options || [];
        
        setMultipleChoiceContent({
          question: content.question || exercise.question || '',
          options: optionsData.length > 0 
            ? optionsData.map(o => ({ text: o.text || o || '' }))
            : [{ text: '' }, { text: '' }, { text: '' }, { text: '' }],
          correctAnswerIndex: content.correctAnswerIndex !== undefined ? content.correctAnswerIndex : (exercise.correctAnswerIndex || 0),
          textColor: content.textColor || exercise.textColor || '#7c3aed',
          fontSize: content.fontSize || exercise.fontSize || 'text-2xl'
        });
      }
    }
  }, [exercise]);

  const handlePairChange = (index, field, value) => {
    const newPairs = [...pairs];
    newPairs[index] = { ...newPairs[index], [field]: value };
    setPairs(newPairs);
  };

  const addPair = () => {
    setPairs([...pairs, { german: '', russian: '', germanTextColor: '#7c3aed', germanFontSize: 'text-2xl', russianTextColor: '#000000', russianFontSize: 'text-2xl' }]);
    setCurrentPairIndex(pairs.length);
  };

  const removePair = (index) => {
    if (pairs.length === 1) {
      setError(t('needAtLeastOneCard'));
      return;
    }
    const newPairs = pairs.filter((_, i) => i !== index);
    setPairs(newPairs);
    if (currentPairIndex >= newPairs.length) {
      setCurrentPairIndex(newPairs.length - 1);
    }
  };

  const handleSave = async () => {
    if (exerciseType === 'flashcard') {
      if (pairs.length === 0 || !pairs[0].german.trim()) {
        setError(t('addAtLeastOneCard'));
        return;
      }
    } else if (exerciseType === 'imageChoice') {
      if (!imageChoiceContent.question.trim()) {
        setError(t('enterQuestion'));
        return;
      }
      if (!imageChoiceContent.questionImage) {
        setError(t('selectQuestionImage'));
        return;
      }
      const validOptions = imageChoiceContent.options.filter(o => o.text.trim());
      if (validOptions.length < 2 || validOptions.length > 4) {
        setError(t('needTwoToFourAnswers'));
        return;
      }
    } else if (exerciseType === 'multipleChoice') {
      if (!multipleChoiceContent.question.trim()) {
        setError(t('enterQuestion'));
        return;
      }
      const validOptions = multipleChoiceContent.options.filter(o => o.text && o.text.trim());
      if (validOptions.length < 2) {
        setError(t('minimumTwoAnswers'));
        return;
      }
    }

    setLoading(true);
    setError('');
    try {
      let title = '';
      let exerciseData = {
        topicId,
        type: exerciseType,
        description: '',
        order: 1,
      };

      if (exerciseType === 'flashcard') {
        title = pairs[0].german; // Берем название из немецкого текста первой карточки
        const formattedPairs = pairs.map(p => ({
          german: p.german,
          english: p.russian,
          germanTextColor: p.germanTextColor || '#7c3aed',
          germanFontSize: p.germanFontSize || 'text-2xl',
          russianTextColor: p.russianTextColor || '#000000',
          russianFontSize: p.russianFontSize || 'text-2xl',
          germanDescription: p.germanDescription || '',
          germanDescriptionColor: p.germanDescriptionColor || '#000000',
          germanDescriptionFontSize: p.germanDescriptionFontSize || 'text-base',
          russianDescription: p.russianDescription || '',
          russianDescriptionColor: p.russianDescriptionColor || '#000000',
          russianDescriptionFontSize: p.russianDescriptionFontSize || 'text-base'
        }));
        exerciseData.title = title;
        exerciseData.content = {
          question: title,
          correctAnswer: pairs[0].german,
          points: 10,
          pairs: formattedPairs,
        };
      } else if (exerciseType === 'imageChoice') {
        title = imageChoiceContent.question; // Берем название из вопроса
        const validOptions = imageChoiceContent.options.filter(o => o.text.trim());
        exerciseData.title = title;
        exerciseData.content = {
          question: imageChoiceContent.question,
          questionImage: imageChoiceContent.questionImage,
          textColor: imageChoiceContent.textColor || '#7c3aed',
          fontSize: imageChoiceContent.fontSize || 'text-2xl',
          options: validOptions.map(o => ({
            text: o.text,
            image: o.image || null,
            description: o.description || ''
          })),
          correctAnswerIndex: imageChoiceContent.correctAnswerIndex,
        };
      } else if (exerciseType === 'multipleChoice') {
        title = multipleChoiceContent.question;
        const validOptions = multipleChoiceContent.options.filter(o => o.text && o.text.trim());
        exerciseData.title = title;
        // Пересчитываем индекс правильного ответа относительно validOptions
        let originalIndex = multipleChoiceContent.correctAnswerIndex;
        let nonEmptySeen = -1;
        let newCorrectIndex = 0;
        let correctAnswerText = '';
        for (let i = 0; i < multipleChoiceContent.options.length; i++) {
          if (multipleChoiceContent.options[i].text && multipleChoiceContent.options[i].text.trim()) {
            nonEmptySeen++;
            if (i === originalIndex) {
              newCorrectIndex = nonEmptySeen;
              correctAnswerText = multipleChoiceContent.options[i].text;
              break;
            }
          }
        }
        exerciseData.content = {
          question: multipleChoiceContent.question,
          textColor: multipleChoiceContent.textColor || '#7c3aed',
          fontSize: multipleChoiceContent.fontSize || 'text-2xl',
          options: validOptions.map(o => ({ text: o.text })),
          correctAnswerIndex: newCorrectIndex,
          correctAnswerText: correctAnswerText
        };
      }

      if (exercise && exercise.id) {
        await exerciseAPI.updateExercise(exercise.id, exerciseData);
      } else {
        await exerciseAPI.createExercise(exerciseData);
      }
      onSave();
    } catch (err) {
      setError(t('errorPrefix') + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!exercise || !exercise.id) return;
    const confirmText = t('deleteCardConfirm');
    if (!window.confirm(confirmText)) return;

    setLoading(true);
    try {
      await exerciseAPI.deleteExercise(exercise.id);
      onSave();
    } catch (err) {
      setError(t('deletionErrorPrefix') + err.message);
    } finally {
      setLoading(false);
    }
  };

  // File upload handlers
  const handleFileUpload = (file, callback) => {
    if (!file) return;
    
    // Validate file type
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError(t('onlyPngOrJpeg'));
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('fileTooLarge'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      callback(e.target.result);
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const handleImageChoiceOptionChange = (index, field, value) => {
    const newOptions = [...imageChoiceContent.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setImageChoiceContent({ ...imageChoiceContent, options: newOptions });
  };

  const handleAddImageChoiceOption = () => {
    if (imageChoiceContent.options.length < 4) {
      setImageChoiceContent({
        ...imageChoiceContent,
        options: [...imageChoiceContent.options, { text: '', image: null, description: '' }]
      });
    }
  };

  const handleRemoveImageChoiceOption = (index) => {
    if (imageChoiceContent.options.length > 2) {
      const newOptions = imageChoiceContent.options.filter((_, i) => i !== index);
      setImageChoiceContent({
        ...imageChoiceContent,
        options: newOptions,
        correctAnswerIndex: Math.min(imageChoiceContent.correctAnswerIndex, newOptions.length - 1)
      });
    }
  };

  // MultipleChoice handlers
  const handleMultipleChoiceOptionChange = (index, value) => {
    const newOptions = [...multipleChoiceContent.options];
    newOptions[index] = { text: value };
    setMultipleChoiceContent({ ...multipleChoiceContent, options: newOptions });
  };

  const handleAddMultipleChoiceOption = () => {
    if (multipleChoiceContent.options.length < 6) {
      setMultipleChoiceContent({
        ...multipleChoiceContent,
        options: [...multipleChoiceContent.options, { text: '' }]
      });
    }
  };

  const handleRemoveMultipleChoiceOption = (index) => {
    if (multipleChoiceContent.options.length > 2) {
      const newOptions = multipleChoiceContent.options.filter((_, i) => i !== index);
      setMultipleChoiceContent({
        ...multipleChoiceContent,
        options: newOptions,
        correctAnswerIndex: Math.min(multipleChoiceContent.correctAnswerIndex, newOptions.length - 1)
      });
    }
  };

  const currentPair = pairs[currentPairIndex] || { german: '', russian: '' };
  const texts = {
    close: t('closeButton'),
    save: t('saveButton'),
    card: t('card'),
    frontSide: t('frontSide'),
    backSide: t('backSide'),
    textDe: t('textDe'),
    translation: t('translation'),
    addCard: t('addCard'),
    deleteCard: t('delete'),
    deckName: t('deckName'),
  };

  return (
    <div className="exercise-editor-modal">
      {/* Header */}
      <div className="exercise-editor-header">
        <button
          onClick={onClose}
          className="exercise-editor-close"
        >
          {texts.close}
        </button>
        <h2 className="exercise-editor-title">{texts.card}</h2>
        <button
          onClick={handleSave}
          disabled={loading}
          className="exercise-editor-save"
        >
          {texts.save}
        </button>
      </div>

      {/* Type Selector */}
      <div className="exercise-editor-type-selector">
        <div className="flex gap-4 items-center">
          <label className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
            {t('exerciseType')}:
          </label>
          <select
            value={exerciseType}
            onChange={(e) => setExerciseType(e.target.value)}
            className="px-4 py-2 bg-black bg-opacity-40 border border-gray-700 rounded-lg text-white font-medium focus:outline-none focus:border-yellow-500 transition-colors"
          >
            <option value="flashcard">{t('flashcard')}</option>
            <option value="imageChoice">{t('imageChoice')}</option>
            <option value="multipleChoice">{t('multipleChoice')}</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-8 mt-4 px-6 py-4 bg-red-900 bg-opacity-30 border border-red-500 rounded-xl text-red-400 font-medium">
          {error}
        </div>
      )}

      {/* Content Area with Scrolling */}
      <div className="exercise-editor-content">
        {/* Flashcard UI */}
        {exerciseType === 'flashcard' ? (
          <>
            {/* Card Preview - Two Column Layout */}
            <div className="grid grid-cols-2 gap-8 max-w-7xl mx-auto">
            {/* Left Column - German Side */}
            <div className="flex flex-col space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">🇩🇪</span>
                <h3 className="text-xs font-bold uppercase tracking-widest text-yellow-400">
                  {texts.textDe}
                </h3>
              </div>
              
              {/* German Card */}
              <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl border border-gray-700 shadow-2xl p-8 min-h-[400px] flex flex-col">
                {/* Main German Text Input */}
                <textarea
                  value={currentPair.german}
                  onChange={(e) => handlePairChange(currentPairIndex, 'german', e.target.value)}
                  placeholder={t('germanExample')}
                  style={{ color: currentPair.germanTextColor }}
                  className={`w-full bg-black bg-opacity-30 border border-gray-600 px-4 py-3 rounded-xl focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-30 focus:outline-none ${currentPair.germanFontSize} font-bold text-center resize-none placeholder-gray-500 transition-all`}
                  rows={3}
                />

                {/* Color Picker for German Text */}
                <div className="mt-6 space-y-3">
                  <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block">
                    {t('textColorLabel')}
                  </label>
                  <ColorPalette
                    selectedColor={currentPair.germanTextColor}
                    onSelectColor={(color) => handlePairChange(currentPairIndex, 'germanTextColor', color)}
                    size="w-8 h-8"
                  />
                </div>

                {/* Font Size Selector */}
                <div className="mt-4">
                  <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-2">
                    {t('fontSizeLabel')}
                  </label>
                  <select
                    value={currentPair.germanFontSize}
                    onChange={(e) => handlePairChange(currentPairIndex, 'germanFontSize', e.target.value)}
                    className="w-full px-3 py-2 bg-black bg-opacity-40 border border-gray-700 rounded-lg text-white text-sm font-medium focus:outline-none focus:border-yellow-500 transition-colors"
                  >
                    {FONT_SIZE_OPTIONS.map(opt => (
                      <option key={opt.token} value={opt.token}>{t(opt.labelKey)}</option>
                    ))}
                  </select>
                </div>

                {/* German Description */}
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-2">
                    {t('descriptionOptionalLabel')}
                  </label>
                  <textarea
                    value={currentPair.germanDescription}
                    onChange={(e) => handlePairChange(currentPairIndex, 'germanDescription', e.target.value)}
                    placeholder={t('germanDescriptionPlaceholder')}
                    style={{ color: currentPair.germanDescriptionColor }}
                    className={`w-full bg-black bg-opacity-30 border border-gray-600 px-4 py-3 rounded-lg focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-30 focus:outline-none ${currentPair.germanDescriptionFontSize} resize-none placeholder-gray-500 transition-all`}
                    rows={2}
                  />

                  {/* Description Controls */}
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1.5">
                        {t('descriptionColorLabel')}
                      </label>
                      <ColorPalette
                        selectedColor={currentPair.germanDescriptionColor}
                        onSelectColor={(color) => handlePairChange(currentPairIndex, 'germanDescriptionColor', color)}
                        size="w-7 h-7"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-gray-500 block mb-1.5">
                        {t('sizeLabel')}
                      </label>
                      <select
                        value={currentPair.germanDescriptionFontSize}
                        onChange={(e) => handlePairChange(currentPairIndex, 'germanDescriptionFontSize', e.target.value)}
                        className="w-full px-2 py-1.5 bg-black bg-opacity-40 border border-gray-700 rounded text-white text-xs focus:outline-none focus:border-yellow-500"
                      >
                        {FONT_SIZE_OPTIONS.map(opt => (
                          <option key={opt.token} value={opt.token}>{t(opt.labelKey)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Translation Side */}
            <div className="flex flex-col space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">🇷🇺</span>
                <h3 className="text-xs font-bold uppercase tracking-widest text-mint-400" style={{ color: '#4ecdc4' }}>
                  {texts.translation}
                </h3>
              </div>

              {/* Translation Card */}
              <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl border border-gray-700 shadow-2xl p-8 min-h-[400px] flex flex-col">
                {/* Display German Text (Read-only) */}
                <div className="mb-6 pb-6 border-b border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-base">🇩🇪</span>
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {t('original') || 'Оригинал'}
                    </span>
                  </div>
                  <div style={{ color: currentPair.germanTextColor }} className={`${currentPair.germanFontSize} font-bold text-center`}>
                    {currentPair.german || <span className="text-gray-600 italic text-base">{t('enterTextOnLeft')}</span>}
                  </div>
                </div>

                {/* Translation Input */}
                <textarea
                  value={currentPair.russian}
                  onChange={(e) => handlePairChange(currentPairIndex, 'russian', e.target.value)}
                  placeholder={t('translationExample')}
                  style={{ color: currentPair.russianTextColor }}
                  className={`w-full bg-black bg-opacity-30 border border-gray-600 px-4 py-3 rounded-xl focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-30 focus:outline-none ${currentPair.russianFontSize} font-bold text-center resize-none placeholder-gray-500 transition-all`}
                  rows={3}
                />

                {/* Color and Font Size for Translation */}
                <div className="mt-6 space-y-3">
                  <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block">
                    {t('textColorLabel')}
                  </label>
                  <ColorPalette
                    selectedColor={currentPair.russianTextColor}
                    onSelectColor={(color) => handlePairChange(currentPairIndex, 'russianTextColor', color)}
                    size="w-8 h-8"
                  />
                </div>

                <div className="mt-4">
                  <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-2">
                    {t('fontSizeLabel')}
                  </label>
                  <select
                    value={currentPair.russianFontSize}
                    onChange={(e) => handlePairChange(currentPairIndex, 'russianFontSize', e.target.value)}
                    className="w-full px-3 py-2 bg-black bg-opacity-40 border border-gray-700 rounded-lg text-white text-sm font-medium focus:outline-none focus:border-mint-500 transition-colors"
                  >
                    {FONT_SIZE_OPTIONS.map(opt => (
                      <option key={opt.token} value={opt.token}>{t(opt.labelKey)}</option>
                    ))}
                  </select>
                </div>

                {/* Russian Description */}
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-2">
                    {t('descriptionOptionalLabel')}
                  </label>
                  <textarea
                    value={currentPair.russianDescription}
                    onChange={(e) => handlePairChange(currentPairIndex, 'russianDescription', e.target.value)}
                    placeholder={t('translationDescriptionPlaceholder')}
                    style={{ color: currentPair.russianDescriptionColor }}
                    className={`w-full bg-black bg-opacity-30 border border-gray-600 px-4 py-3 rounded-lg focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-30 focus:outline-none ${currentPair.russianDescriptionFontSize} resize-none placeholder-gray-500 transition-all`}
                    rows={2}
                  />

                  {/* Description Controls */}
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1.5">
                        {t('descriptionColorLabel')}
                      </label>
                      <ColorPalette
                        selectedColor={currentPair.russianDescriptionColor}
                        onSelectColor={(color) => handlePairChange(currentPairIndex, 'russianDescriptionColor', color)}
                        size="w-7 h-7"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-gray-500 block mb-1.5">
                        {t('sizeLabel')}
                      </label>
                      <select
                        value={currentPair.russianDescriptionFontSize}
                        onChange={(e) => handlePairChange(currentPairIndex, 'russianDescriptionFontSize', e.target.value)}
                        className="w-full px-2 py-1.5 bg-black bg-opacity-40 border border-gray-700 rounded text-white text-xs focus:outline-none focus:border-mint-500"
                      >
                        {FONT_SIZE_OPTIONS.map(opt => (
                          <option key={opt.token} value={opt.token}>{t(opt.labelKey)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : exerciseType === 'imageChoice' ? (
        /* ImageChoice UI - Dark Theme */
        <div className="exercise-editor-content flex-1 overflow-y-auto px-8 py-6">
          <div className="max-w-4xl mx-auto space-y-8">
            
            {/* Question Section */}
            <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl border border-gray-700 shadow-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl" style={{ background: 'linear-gradient(135deg, #e8b84b 0%, #f5d485 100%)' }}>
                  ❓
                </div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-yellow-400">
                  {t('question')}
                </h3>
              </div>
              
              <textarea
                value={imageChoiceContent.question}
                onChange={(e) => setImageChoiceContent({ ...imageChoiceContent, question: e.target.value })}
                placeholder={t('enterQuestion')}
                style={{ color: imageChoiceContent.textColor }}
                className={`w-full bg-black bg-opacity-30 border border-gray-600 px-4 py-3 rounded-xl focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-30 focus:outline-none ${imageChoiceContent.fontSize} font-bold text-white placeholder-gray-500 transition-all resize-none`}
                rows={3}
              />
              
              {/* Color and Font Size Selectors */}
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-xs font-bold mb-2 block uppercase tracking-wide">
                    {t('textColorLabel')}
                  </label>
                  <ColorPalette
                    selectedColor={imageChoiceContent.textColor}
                    onSelectColor={(color) => setImageChoiceContent({ ...imageChoiceContent, textColor: color })}
                    size="w-8 h-8"
                  />
                </div>
                
                <div>
                  <label className="text-gray-400 text-xs font-bold mb-2 block uppercase tracking-wide">
                    {t('fontSizeLabel')}
                  </label>
                  <select
                    value={imageChoiceContent.fontSize}
                    onChange={(e) => setImageChoiceContent({ ...imageChoiceContent, fontSize: e.target.value })}
                    className="w-full px-3 py-2 bg-black bg-opacity-40 border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-30 focus:outline-none text-sm transition-all"
                  >
                    {FONT_SIZE_OPTIONS.map(opt => (
                      <option key={opt.token} value={opt.token}>{t(opt.labelKey)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Question Image Upload */}
            <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl border border-gray-700 shadow-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl" style={{ background: 'linear-gradient(135deg, #4ecdc4 0%, #78d8ce 100%)' }}>
                  🖼️
                </div>
                <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: '#4ecdc4' }}>
                  {t('questionImage')}
                </h3>
              </div>
              
              <div className="border-2 border-dashed rounded-xl p-4 border-gray-600 bg-black bg-opacity-20">
                {imageChoiceContent.questionImage ? (
                  <div className="space-y-3">
                    <img src={imageChoiceContent.questionImage} alt="Question" className="w-full h-56 object-cover rounded-lg shadow-lg" />
                    <button
                      onClick={() => setImageChoiceContent({ ...imageChoiceContent, questionImage: null })}
                      className="w-full px-4 py-2.5 bg-red-900 bg-opacity-30 hover:bg-opacity-50 border border-red-500 text-red-300 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-all"
                    >
                      <X size={16} />
                      {t('delete')}
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={(e) => handleFileUpload(e.target.files?.[0], (base64) => {
                        setImageChoiceContent({ ...imageChoiceContent, questionImage: base64 });
                      })}
                      className="hidden"
                    />
                    <div className="flex flex-col items-center justify-center gap-3 py-8 hover:bg-gray-800 hover:bg-opacity-30 rounded-lg transition-all">
                      <Upload size={32} className="text-gray-500" />
                      <span className="font-bold text-gray-400 text-sm uppercase tracking-wider">
                        {t('loadImage')}
                      </span>
                    </div>
                  </label>
                )}
              </div>
            </div>

            {/* Options Section */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl" style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)' }}>
                    📝
                  </div>
                  <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: '#ff6b6b' }}>
                    {t('answerOptions')} <span className="text-yellow-400">({imageChoiceContent.options.filter(o => o.text.trim()).length}/4)</span>
                  </h3>
                </div>
                {imageChoiceContent.options.length < 4 && (
                  <button
                    onClick={handleAddImageChoiceOption}
                    className="px-4 py-2 bg-yellow-900 bg-opacity-30 hover:bg-opacity-50 border border-yellow-500 text-yellow-300 rounded-lg font-bold text-sm flex items-center gap-2 transition-all"
                  >
                    <Plus size={16} />
                    {t('addAnswer')}
                  </button>
                )}
              </div>
              
              <div className="space-y-4">
                {imageChoiceContent.options.map((option, index) => (
                  <div key={index} className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl border border-gray-700 shadow-2xl p-5">
                    <div className="flex gap-4 items-start mb-4">
                      <div className="mt-2">
                        <input
                          type="radio"
                          name="correctAnswer"
                          checked={imageChoiceContent.correctAnswerIndex === index}
                          onChange={() => setImageChoiceContent({ ...imageChoiceContent, correctAnswerIndex: index })}
                          className="w-5 h-5 text-yellow-400 bg-gray-700 border-gray-600 focus:ring-2 focus:ring-yellow-400 cursor-pointer"
                        />
                      </div>
                      
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-yellow-900 bg-opacity-30 border border-yellow-500 text-yellow-300 rounded text-xs font-bold">
                            {t('answerLabel')} {index + 1}
                          </span>
                          {imageChoiceContent.correctAnswerIndex === index && (
                            <span className="px-2 py-1 bg-green-900 bg-opacity-30 border border-green-500 text-green-300 rounded text-xs font-bold">
                              ✓ {t('correct') || 'Правильная'}
                            </span>
                          )}
                        </div>
                        
                        <input
                          type="text"
                          value={option.text}
                          onChange={(e) => handleImageChoiceOptionChange(index, 'text', e.target.value)}
                          placeholder={`${t('answerLabel')} ${index + 1}`}
                          className="w-full bg-black bg-opacity-30 border border-gray-600 text-white px-4 py-2.5 rounded-lg focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-30 focus:outline-none placeholder-gray-500 font-bold transition-all"
                        />
                        
                        <input
                          type="text"
                          value={option.description}
                          onChange={(e) => handleImageChoiceOptionChange(index, 'description', e.target.value)}
                          placeholder={t('answerDescription')}
                          className="w-full bg-black bg-opacity-20 border border-gray-700 text-gray-300 px-4 py-2 rounded-lg focus:border-gray-500 focus:outline-none placeholder-gray-600 text-sm transition-all"
                        />
                      </div>
                      
                      {imageChoiceContent.options.length > 2 && (
                        <button
                          onClick={() => handleRemoveImageChoiceOption(index)}
                          className="px-3 py-2 bg-red-900 bg-opacity-30 hover:bg-opacity-50 border border-red-500 text-red-300 rounded-lg font-bold text-sm transition-all"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                    
                    {/* Option Image */}
                    {option.image ? (
                      <div className="space-y-2 mt-3 pt-3 border-t border-gray-700">
                        <img src={option.image} alt={`Option ${index + 1}`} className="w-full h-32 object-cover rounded-lg shadow-lg" />
                        <button
                          onClick={() => handleImageChoiceOptionChange(index, 'image', null)}
                          className="w-full px-3 py-2 bg-red-900 bg-opacity-30 hover:bg-opacity-50 border border-red-500 text-red-300 rounded-lg font-bold text-xs flex items-center justify-center gap-2 transition-all"
                        >
                          <X size={14} />
                          {t('delete')}
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block mt-3 pt-3 border-t border-gray-700">
                        <input
                          type="file"
                          accept="image/png,image/jpeg"
                          onChange={(e) => handleFileUpload(e.target.files?.[0], (base64) => {
                            handleImageChoiceOptionChange(index, 'image', base64);
                          })}
                          className="hidden"
                        />
                        <div className="border-2 border-dashed rounded-lg p-3 text-center border-gray-600 hover:border-gray-500 hover:bg-gray-800 hover:bg-opacity-30 transition-all">
                          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                            {t('uploadImageOptional')}
                          </span>
                        </div>
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : exerciseType === 'multipleChoice' ? (
        /* MultipleChoice UI - Dark Theme */
        <div className="exercise-editor-content flex-1 overflow-y-auto px-8 py-6">
          <div className="max-w-4xl mx-auto space-y-8">
            
            {/* Question Section (German Word) */}
            <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl border border-gray-700 shadow-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl" style={{ background: 'linear-gradient(135deg, #e8b84b 0%, #f5d485 100%)' }}>
                  🇩🇪
                </div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-yellow-400">
                  {t('germanText')} ({t('question')})
                </h3>
              </div>
              
              <input
                type="text"
                value={multipleChoiceContent.question}
                onChange={(e) => setMultipleChoiceContent({ ...multipleChoiceContent, question: e.target.value })}
                placeholder={t('exampleGermanPhrase')}
              className="w-full bg-black bg-opacity-30 border border-gray-600 px-6 py-4 rounded-xl focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-30 focus:outline-none font-bold placeholder-gray-600 transition-all"
              style={{ fontFamily: 'serif', color: multipleChoiceContent.textColor, fontSize: resolveFontSize(multipleChoiceContent.fontSize) }}
              />
              
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-2">
                      {t('textColorLabel')}
                    </label>
                    <ColorPalette
                      selectedColor={multipleChoiceContent.textColor}
                      onSelectColor={(color) => setMultipleChoiceContent({ ...multipleChoiceContent, textColor: color })}
                      size="w-8 h-8"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-2">
                      {t('fontSizeLabel')}
                    </label>
                    <select
                      value={multipleChoiceContent.fontSize}
                      onChange={(e) => setMultipleChoiceContent({ ...multipleChoiceContent, fontSize: e.target.value })}
                      className="w-full px-3 py-2 bg-black bg-opacity-40 border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-30 focus:outline-none text-sm transition-all"
                    >
                      {FONT_SIZE_OPTIONS.map(opt => (
                        <option key={opt.token} value={opt.token}>{t(opt.labelKey)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              
              <p className="mt-3 text-sm text-gray-500">
                {t('enterGermanWordForChoice')}
              </p>
            </div>

            {/* Answer Options Section */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl" style={{ background: 'linear-gradient(135deg, #4ecdc4 0%, #78d8ce 100%)' }}>
                    📝
                  </div>
                  <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: '#4ecdc4' }}>
                    {t('answerOptions')} <span className="text-yellow-400">({multipleChoiceContent.options.filter(o => o.text && o.text.trim()).length})</span>
                  </h3>
                </div>
                {multipleChoiceContent.options.length < 6 && (
                  <button
                    onClick={handleAddMultipleChoiceOption}
                    className="px-4 py-2 bg-teal-900 bg-opacity-30 hover:bg-opacity-50 border border-teal-500 text-teal-300 rounded-lg font-bold text-sm flex items-center gap-2 transition-all"
                  >
                    <Plus size={16} />
                    {t('addAnswer')}
                  </button>
                )}
              </div>
              
              <div className="space-y-3">
                {multipleChoiceContent.options.map((option, index) => (
                  <div key={index} className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-xl border border-gray-700 shadow-lg p-4">
                    <div className="flex gap-3 items-center">
                      <div className="flex flex-col items-center gap-1">
                        <input
                          type="radio"
                          name="correctAnswerMultipleChoice"
                          checked={multipleChoiceContent.correctAnswerIndex === index}
                          onChange={() => setMultipleChoiceContent({ ...multipleChoiceContent, correctAnswerIndex: index })}
                          className="w-5 h-5 text-teal-400 bg-gray-700 border-gray-600 focus:ring-2 focus:ring-teal-400 cursor-pointer"
                        />
                        <span className="text-xs text-gray-500 font-bold">#{index + 1}</span>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {multipleChoiceContent.correctAnswerIndex === index && (
                            <span className="px-2 py-1 bg-green-900 bg-opacity-30 border border-green-500 text-green-300 rounded text-xs font-bold">
                              ✓ Правильный
                            </span>
                          )}
                        </div>
                        
                        <input
                          type="text"
                          value={option.text}
                          onChange={(e) => handleMultipleChoiceOptionChange(index, e.target.value)}
                          placeholder={`${t('answerOption')} ${index + 1}`}
                          className="w-full bg-black bg-opacity-30 border border-gray-600 text-white px-4 py-3 rounded-lg focus:border-teal-400 focus:ring-2 focus:ring-teal-400 focus:ring-opacity-30 focus:outline-none placeholder-gray-500 font-bold text-lg transition-all"
                        />
                        <p className="mt-2 text-xs text-gray-500">
                          Текст ответа (перевод на русский/украинский)
                        </p>
                      </div>
                      
                      {multipleChoiceContent.options.length > 2 && (
                        <button
                          onClick={() => handleRemoveMultipleChoiceOption(index)}
                          className="px-3 py-2 bg-red-900 bg-opacity-30 hover:bg-opacity-50 border border-red-500 text-red-300 rounded-lg font-bold text-sm transition-all"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl border border-gray-700 shadow-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">👁️</span>
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400">
                  {t('previewButton')}
                </h3>
              </div>
              
              <div className="text-center mb-6">
                <div className="mb-4" style={{ fontFamily: 'serif', color: multipleChoiceContent.textColor }}>
                  <div style={{ fontSize: resolveFontSize(multipleChoiceContent.fontSize), fontWeight: 900, color: multipleChoiceContent.textColor }}>
                    {multipleChoiceContent.question || '???'}
                  </div>
                </div>
                <p className="text-gray-500">{t('chooseCorrectOption')}</p>
              </div>
              
              <div className="space-y-2">
                {multipleChoiceContent.options.filter(o => o.text && o.text.trim()).map((option, index) => (
                  <div
                    key={index}
                    className={`px-4 py-3 rounded-lg border-2 text-center font-bold ${
                      multipleChoiceContent.correctAnswerIndex === index
                        ? 'bg-teal-900 bg-opacity-20 border-teal-500 text-teal-300'
                        : 'bg-gray-800 bg-opacity-40 border-gray-700 text-gray-400'
                    }`}
                  >
                    {option.text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      </div>
    </div>
  );
};
